/**
 * SurveyTemplatesPage.tsx
 *
 * Form / survey template listing. Displays all active surveys with
 * inline search and summary statistics.
 *
 * Lives at /surveys/templates after the legacy-parity refactor that moved
 * /surveys/all to the patient submissions inbox (see FormsInboxPage).
 */
import { useState, useEffect, useMemo } from 'react';
import { usePageContext } from '@/hooks/usePageContext';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Badge } from '@/components/ui/Badge';
import type { BadgeVariant } from '@/types/common.types';
import { Search } from 'lucide-react';
import { listSurveys, type Survey } from '@/services/surveys.service';
import { SurveyEFormModal } from './SurveyEFormModal';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  active: 'success',
  inactive: 'neutral',
};

// ─── Constants ─────────────────────────────────────────────────────────────────

function hasExcessiveHyphens(title: string): boolean {
  let count = 0;
  for (const ch of title) {
    if (ch === '-') {
      count++;
      if (count >= 4) return true;
    }
  }
  return false;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SurveyTemplatesPage() {
  const updateAIContext = usePageContext('Forms');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['surveys-templates-all'],
    queryFn: () => listSurveys({ page: 1, perPage: 5000 }),
    staleTime: 30_000,
  });

  const allSurveys = useMemo(() => {
    const raw = data?.data ?? [];
    return raw.filter((s) => !hasExcessiveHyphens(s.title));
  }, [data]);

  const surveys = useMemo(() => {
    let filtered = allSurveys;
    if (!showInactive) {
      filtered = filtered.filter((s) => s.isActive);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter((s) => s.title.toLowerCase().includes(q));
    }
    return filtered;
  }, [allSurveys, showInactive, search]);

  const totalSurveys = surveys.length;
  const totalResponses = surveys.reduce((sum, s) => sum + (s.responseCount ?? 0), 0);
  const surveysWithQuestions = surveys.filter((s) => (s.questionCount ?? 0) > 0);
  const avgCompletionRate = surveysWithQuestions.length > 0
    ? Math.round(
        (surveys.filter((s) => (s.responseCount ?? 0) > 0).length / Math.max(surveysWithQuestions.length, 1)) * 100,
      )
    : 0;

  useEffect(() => {
    updateAIContext({
      filters: `Search: ${search || 'none'}, Show inactive: ${showInactive}`,
      dataSummary: `${totalSurveys} surveys, ${totalResponses} responses`,
    });
  }, [search, showInactive, totalSurveys, totalResponses, updateAIContext]);

  const columns = useMemo<DataTableColumn<Survey>[]>(() => [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-800">{row.title}</span>,
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <Badge variant={STATUS_VARIANTS[row.isActive ? 'active' : 'inactive'] ?? 'neutral'} dot>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'questionCount',
      header: 'Questions',
      sortable: true,
      render: (row) => <span className="text-slate-700">{row.questionCount ?? 0}</span>,
    },
    {
      key: 'responseCount',
      header: 'Responses',
      sortable: true,
      render: (row) => <span className="text-slate-700">{row.responseCount ?? 0}</span>,
    },
    {
      key: 'dateCreated',
      header: 'Created',
      sortable: true,
      render: (row) => <span className="text-slate-600">{formatDate(row.dateCreated)}</span>,
    },
    {
      key: 'completionRate',
      header: 'Completion Rate',
      render: (row) => {
        const hasQuestions = (row.questionCount ?? 0) > 0;
        const hasResponses = (row.responseCount ?? 0) > 0;
        const rate = hasQuestions && hasResponses ? '100%' : hasQuestions ? '0%' : '\u2014';
        return <span className="text-slate-600">{rate}</span>;
      },
    },
  ], []);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-6 p-6">
        <PageHeader
          variant="compact"
          title="Forms"
          actions={(
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <button
                  type="button"
                  role="switch"
                  aria-checked={showInactive}
                  onClick={() => setShowInactive((v) => !v)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${showInactive ? 'bg-primary' : 'bg-slate-300'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${showInactive ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                </button>
                Show inactive
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search forms..."
                  className="h-9 w-56 rounded-md border border-slate-300 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          )}
        />

        <MetricCardGrid columns={3}>
          <StatCard label="Total Forms" value={totalSurveys} />
          <StatCard label="Total Responses" value={totalResponses} />
          <StatCard label="Avg Completion Rate" value={`${avgCompletionRate}%`} />
        </MetricCardGrid>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable<Survey>
          data={surveys}
          columns={columns}
          keyField="id"
          loading={isLoading}
          page={1}
          pageSize={surveys.length || 1}
          total={surveys.length}
          onPageChange={() => {}}
          onRowClick={(row) => { if (row.isActive) setSelectedSurvey(row); }}
          emptyTitle="No forms found"
          emptyDescription="Try adjusting your search or create a new form."
        />
      </div>

      <SurveyEFormModal
        survey={selectedSurvey}
        open={selectedSurvey !== null}
        onClose={() => setSelectedSurvey(null)}
      />
    </div>
  );
}

export default SurveyTemplatesPage;
